<template>
  <div class="workspace-tasks-tab">
    <!-- Search Toolbar -->
    <div v-if="showSearch" class="workspace-toolbar">
      <div class="search-box">
        <span class="search-icon">🔍</span>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('workspace.search')"
          class="search-input"
        />
      </div>
    </div>

    <!-- Advanced Filters Panel (only shown when showFilters is true) -->
    <Transition name="slide-down">
      <div v-if="showFilters && showAdvancedFilters" class="filter-panel">
        <div class="filter-panel-header">
          <h3>{{ $t('workspace.filters.title') }}</h3>
          <button v-if="hasActiveFilters" class="btn-text" @click="clearFilters">
            {{ $t('workspace.filters.clearAll') }}
          </button>
        </div>

        <div class="filter-grid">
          <!-- Status Filter -->
          <div class="filter-group">
            <label>{{ $t('workspace.filters.status') }}</label>
            <select v-model="filters.status" class="filter-select">
              <option :value="null">{{ $t('workspace.filters.allStatuses') }}</option>
              <option value="PENDING">{{ $t('tasks.status.PENDING') }}</option>
              <option value="IN_PROGRESS">{{ $t('tasks.status.IN_PROGRESS') }}</option>
              <option value="COMPLETED">{{ $t('tasks.status.COMPLETED') }}</option>
              <option value="ON_HOLD">{{ $t('tasks.status.ON_HOLD') }}</option>
              <option value="CANCELLED">{{ $t('tasks.status.CANCELLED') }}</option>
            </select>
          </div>

          <!-- Date Range Filter -->
          <div class="filter-group">
            <label>{{ $t('workspace.filters.dateRange') }}</label>
            <input
              v-model="filters.startDate"
              type="date"
              :placeholder="$t('workspace.filters.startDate')"
              class="filter-input"
            />
            <input
              v-model="filters.endDate"
              type="date"
              :placeholder="$t('workspace.filters.endDate')"
              class="filter-input"
            />
          </div>

          <!-- Budget Range Filter -->
          <div class="filter-group">
            <label>{{ $t('workspace.filters.budgetRange') }}</label>
            <input
              v-model.number="filters.minAmount"
              type="number"
              :placeholder="$t('workspace.filters.minAmount')"
              class="filter-input"
            />
            <input
              v-model.number="filters.maxAmount"
              type="number"
              :placeholder="$t('workspace.filters.maxAmount')"
              class="filter-input"
            />
          </div>

          <!-- Assignment Filter -->
          <div class="filter-group">
            <label>{{ $t('workspace.filters.assignment') }}</label>
            <select v-model="filters.assignment" class="filter-select">
              <option :value="null">{{ $t('workspace.filters.allTasks') }}</option>
              <option value="assigned">{{ $t('workspace.filters.assigned') }}</option>
              <option value="unassigned">{{ $t('workspace.filters.unassigned') }}</option>
            </select>
          </div>

          <!-- Phase Filter (only for multi-phase projects) -->
          <div v-if="showPhaseColumn && phases.length" class="filter-group">
            <label>{{ $t('workspace.filters.phase') }}</label>
            <select v-model="filters.phaseId" class="filter-select">
              <option :value="null">{{ $t('workspace.filters.allPhases') }}</option>
              <option v-for="phase in phases" :key="phase.id" :value="phase.id">
                {{ phase.name }}
              </option>
            </select>
          </div>

          <!-- Intervenant Filter -->
          <div class="filter-group">
            <label>{{ $t('workspace.filters.intervenant') }}</label>
            <select v-model="filters.intervenantId" class="filter-select">
              <option :value="null">{{ $t('workspace.filters.allIntervenants') }}</option>
              <option v-for="intervenant in allIntervenants" :key="intervenant.id" :value="intervenant.id">
                {{ intervenant.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Tasks Data Grid -->
    <div class="data-grid">
      <AppDataGrid
        :row-data="taskGridData"
        :column-defs="taskColumnDefs"
        :actions="taskActions"
        :pagination="true"
        :page-size="10"
        :page-size-options="itemsPerPageOptions"
        :suppress-pagination-panel="false"
        :show-search="false"
        grid-class="tasks-grid"
        grid-style="width: 100%;"
        dom-layout="autoHeight"
        :default-col-def="{ wrapText: false, autoHeight: false }"
        @row-clicked="handleTaskRowClick"
        @action-clicked="handleTaskAction"
      />
    </div>

    <!-- Task Form Modal -->
    <TaskFormModal
      :show="showTaskModal"
      :task="editingTask"
      :project-id="projectId"
      :project="project"
      :phases="phases"
      :intervenants="allIntervenants"
      @close="closeTaskModal"
      @saved="handleTaskSaved"
    />

    <!-- Task Status Transition Modal -->
    <TaskStatusTransitionModal
      v-if="selectedTaskForStatusChange"
      :is-visible="showTaskStatusModal"
      :task-id="selectedTaskForStatusChange.id!"
      :project-id="projectId"
      :current-status="selectedTaskForStatusChange.status"
      @close="showTaskStatusModal = false; selectedTaskForStatusChange = null"
      @success="handleTaskStatusChanged"
    />

    <!-- Delete Confirmation Modal - Task -->
    <ConfirmModal
      v-if="showDeleteConfirm"
      :isVisible="showDeleteConfirm"
      :title="$t('tasks.deleteTask')"
      :message="$t('workspace.confirmDelete.task')"
      :details="deleteTaskName"
      :confirm-text="$t('common.delete')"
      :cancel-text="$t('common.cancel')"
      type="danger"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifications } from '@/composables/useNotifications'
import { usePermissions } from '@/composables/usePermissions'
import AppDataGrid from '@/components/base/AppDataGrid.vue'
import TaskFormModal from '../modals/TaskFormModal.vue'
import TaskStatusTransitionModal from '@/components/project/task/TaskStatusTransitionModal.vue'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import projectTasksService from '@/services/projectTasks.service'
import { formatCurrencyWithUserPreference } from '@/utils/currencyUtils'
import { formatDate } from '@/utils/formatters'

// Interfaces
interface Phase {
  id?: number
  projectId: number
  name: string
  description?: string
  plannedStartDate?: string
  plannedEndDate?: string
  actualStartDate?: string
  actualEndDate?: string
  dependencyPhaseId?: number
  dependencyPhaseName?: string
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED'
  isDelivered?: boolean
  deliveredDate?: string
  createdBy?: number
  createdAt?: string
  updatedAt?: string
}

interface Intervenant {
  id?: number
  projectId?: number
  name: string
  type?: string
  email: string
  phone?: string
  address?: string
  createdAt?: string
  updatedAt?: string
}

interface Task {
  id?: number
  projectId?: number
  name: string
  description?: string
  amount: number
  startDate?: string
  endDate?: string
  status?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD'
  assignedTo?: string
  createdBy?: number
  updatedBy?: number
  createdAt?: string
  updatedAt?: string
  intervenantName?: string
  intervenantId?: number
  phaseId?: number
  phaseName?: string
}

interface Project {
  id?: number
  multiPhase?: boolean
  [key: string]: any
}

const { t } = useI18n()
const { notifySuccess, notifyError } = useNotifications()

const props = defineProps<{
  projectId: number
  tasks: Task[]
  allIntervenants: Intervenant[]
  phases?: Phase[]
  project?: Project
  // Configuration options
  showSearch?: boolean
  showFilters?: boolean
  showPhaseColumn?: boolean
  // Optional pre-filtered tasks (for phase-specific filtering)
  filteredTasks?: Task[]
}>()

const emit = defineEmits<{
  taskSelected: [task: Task]
  taskCreated: []
  taskUpdated: []
  taskDeleted: []
}>()

// Provide default values for optional props
const phases = computed(() => props.phases || [])
const showSearch = computed(() => props.showSearch !== false) // Default to true
const showFilters = computed(() => props.showFilters === true) // Default to false
const showPhaseColumn = computed(() => props.showPhaseColumn === true) // Default to false

// State
const selectedTask = ref<Task | null>(null)
const searchQuery = ref('')
const showAdvancedFilters = ref(false)
const showTaskModal = ref(false)
const showTaskStatusModal = ref(false)
const editingTask = ref<Task | null>(null)
const selectedTaskForStatusChange = ref<Task | null>(null)
const showDeleteConfirm = ref(false)
const deleteTaskId = ref<number | null>(null)
const deleteTaskName = ref('')

// Filter state
const filters = ref({
  status: null as string | null,
  startDate: '',
  endDate: '',
  minAmount: null as number | null,
  maxAmount: null as number | null,
  assignment: null as string | null,
  phaseId: null as number | null,
  intervenantId: null as number | null
})

// Pagination configuration for AppDataGrid
const itemsPerPageOptions = [5, 10, 25, 50, 100]

// Reactive formatted amounts
const formattedAmounts = ref<Record<string, string>>({})

// Computed - Get tasks to display (either filtered or all)
const baseTasks = computed(() => {
  return props.filteredTasks || props.tasks
})

// Computed - Apply search and advanced filters
const searchAndFilteredTasks = computed(() => {
  let result = [...baseTasks.value]

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((task: Task) =>
      task.name?.toLowerCase().includes(query) ||
      task.description?.toLowerCase().includes(query) ||
      task.intervenantName?.toLowerCase().includes(query)
    )
  }

  // Apply advanced filters (only if showFilters is true)
  if (showFilters.value) {
    // Status filter
    if (filters.value.status) {
      result = result.filter((task: Task) => task.status === filters.value.status)
    }

    // Date range filter
    if (filters.value.startDate) {
      result = result.filter((task: Task) => 
        task.startDate && task.startDate >= filters.value.startDate
      )
    }
    if (filters.value.endDate) {
      result = result.filter((task: Task) => 
        task.endDate && task.endDate <= filters.value.endDate
      )
    }

    // Budget range filter
    if (filters.value.minAmount !== null) {
      result = result.filter((task: Task) => task.amount >= filters.value.minAmount!)
    }
    if (filters.value.maxAmount !== null) {
      result = result.filter((task: Task) => task.amount <= filters.value.maxAmount!)
    }

    // Assignment filter
    if (filters.value.assignment === 'assigned') {
      result = result.filter((task: Task) => task.intervenantId)
    } else if (filters.value.assignment === 'unassigned') {
      result = result.filter((task: Task) => !task.intervenantId)
    }

    // Phase filter
    if (filters.value.phaseId) {
      result = result.filter((task: Task) => task.phaseId === filters.value.phaseId)
    }

    // Intervenant filter
    if (filters.value.intervenantId) {
      result = result.filter((task: Task) => task.intervenantId === filters.value.intervenantId)
    }
  }

  return result
})

// Computed - Check if any filters are active
const hasActiveFilters = computed(() => {
  return filters.value.status ||
    filters.value.startDate ||
    filters.value.endDate ||
    filters.value.minAmount !== null ||
    filters.value.maxAmount !== null ||
    filters.value.assignment ||
    filters.value.phaseId ||
    filters.value.intervenantId
})

// Methods
const selectTask = (task: Task) => {
  selectedTask.value = task
  emit('taskSelected', task)
}

const editTask = (task: Task) => {
  editingTask.value = { ...task }
  showTaskModal.value = true
}

const openTaskStatusModal = (task: Task) => {
  selectedTaskForStatusChange.value = task
  showTaskStatusModal.value = true
}

const handleTaskStatusChanged = () => {
  showTaskStatusModal.value = false
  selectedTaskForStatusChange.value = null
  emit('taskUpdated')
}

const deleteTask = (id: number) => {
  const task = searchAndFilteredTasks.value.find((t: Task) => t.id === id)
  deleteTaskId.value = id
  deleteTaskName.value = task?.name || ''
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  if (deleteTaskId.value === null) return
  
  try {
    await projectTasksService.deleteTask(
      props.projectId.toString(),
      deleteTaskId.value.toString()
    )
    notifySuccess(t('workspace.success.taskDeleted'))
    emit('taskDeleted')
  } catch (error) {
    notifyError(t('workspace.errors.taskDeleteFailed'))
  } finally {
    cancelDelete()
  }
}

const cancelDelete = () => {
  showDeleteConfirm.value = false
  deleteTaskId.value = null
  deleteTaskName.value = ''
}

const closeTaskModal = () => {
  showTaskModal.value = false
  editingTask.value = null
}

const handleTaskSaved = () => {
  closeTaskModal()
  if (editingTask.value?.id) {
    emit('taskUpdated')
  } else {
    emit('taskCreated')
  }
}

const clearFilters = () => {
  filters.value = {
    status: null,
    startDate: '',
    endDate: '',
    minAmount: null,
    maxAmount: null,
    assignment: null,
    phaseId: null,
    intervenantId: null
  }
}

// Grid Configuration
const { hasPermissionForProject, PermissionType } = usePermissions()

// Helper functions for cell rendering
const getStatusDotColor = (status: string): string => {
  switch (status) {
    case 'completed': return '#10b981'
    case 'in_progress': return '#3b82f6'
    case 'on_hold': return '#f59e0b'
    case 'cancelled': return '#ef4444'
    case 'pending': return '#9ca3af'
    default: return '#9ca3af'
  }
}

const getStatusBadgeColors = (status: string) => {
  switch (status) {
    case 'completed': return { bg: '#d1fae5', text: '#065f46' }
    case 'in_progress': return { bg: '#dbeafe', text: '#1e40af' }
    case 'on_hold': return { bg: '#fed7aa', text: '#92400e' }
    case 'cancelled': return { bg: '#fee2e2', text: '#991b1b' }
    case 'pending': return { bg: '#f3f4f6', text: '#374151' }
    default: return { bg: '#f3f4f6', text: '#374151' }
  }
}

// Custom cell renderers for tasks grid
const taskCellRenderers: Record<string, (params: any) => string> = {
  name: (params: any) => {
    const status = params.data.statusRaw?.toLowerCase() || 'pending'
    return `
      <div style="display: flex; align-items: center; gap: 12px;">
        <span style="
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${getStatusDotColor(status)};
          flex-shrink: 0;
        "></span>
        <strong style="font-weight: 600; color: #1f2937;">${params.value}</strong>
      </div>
    `
  },
  
  intervenant: (params: any) => {
    if (!params.value) return '<span style="color: #9ca3af;">-</span>'
    return `<span style="color: #374151;">${params.value}</span>`
  },
  
  phase: (params: any) => {
    if (!params.value) {
      return `<span style="color: #9ca3af; font-style: italic;">${t('workspace.tasks.unassigned')}</span>`
    }
    return `<span style="color: #374151;">${params.value}</span>`
  },
  
  amount: (params: any) => {
    if (!params.value) return '-'
    return `
      <span style="
        font-weight: 600;
        color: #059669;
        font-size: 15px;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      ">${params.value}</span>
    `
  },
  
  dates: (params: any) => {
    const startDate = params.data.startDate ? formatDate(params.data.startDate) : '-'
    const endDate = params.data.endDate ? formatDate(params.data.endDate) : '-'
    return `
      <div style="display: flex; flex-direction: column; gap: 2px; font-size: 0.875rem;">
        <div style="color: #374151;">${startDate}</div>
        <div style="color: #9ca3af; font-size: 0.75rem;">→</div>
        <div style="color: #374151;">${endDate}</div>
      </div>
    `
  },
  
  status: (params: any) => {
    if (!params.value) return ''
    const status = params.data.statusRaw?.toLowerCase() || 'pending'
    const colors = getStatusBadgeColors(status)
    return `
      <span style="
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 6px 14px;
        border-radius: 20px;
        font-weight: 600;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        background-color: ${colors.bg};
        color: ${colors.text};
        white-space: nowrap;
      ">
        <span style="width: 6px; height: 6px; border-radius: 50%; background-color: currentColor;"></span>
        ${params.value}
      </span>
    `
  }
}

// Prepare task data for grid
const taskGridData = computed(() => {
  return searchAndFilteredTasks.value.map((task: Task) => ({
    id: task.id,
    name: task.name,
    intervenant: task.intervenantName || null,
    phase: task.phaseName || null,
    amount: getFormattedAmount(`task-${task.id}`),
    dates: `${task.startDate || ''} - ${task.endDate || ''}`,
    startDate: task.startDate,
    endDate: task.endDate,
    status: t(`tasks.status.${task.status}`),
    statusRaw: task.status,
    _originalData: task
  }))
})

// Column definitions for tasks grid
const taskColumnDefs = computed(() => {
  const columns = [
    {
      field: 'name',
      headerName: t('workspace.tasks.name'),
      cellRenderer: taskCellRenderers.name,
      minWidth: 180,
      flex: 1,
      sortable: true,
      wrapText: false,
      autoHeight: false
    },
    {
      field: 'intervenant',
      headerName: t('workspace.tasks.intervenant'),
      cellRenderer: taskCellRenderers.intervenant,
      width: 180,
      sortable: true,
      wrapText: false,
      autoHeight: false
    }
  ]
  
  // Add phase column conditionally
  if (showPhaseColumn.value) {
    columns.push({
      field: 'phase',
      headerName: t('workspace.tasks.phase'),
      cellRenderer: taskCellRenderers.phase,
      width: 160,
      sortable: true,
      wrapText: false,
      autoHeight: false
    })
  }
  
  columns.push(
    {
      field: 'amount',
      headerName: t('workspace.tasks.amount'),
      cellRenderer: taskCellRenderers.amount,
      width: 140,
      sortable: true,
      wrapText: false,
      autoHeight: false
    },
    {
      field: 'dates',
      headerName: t('workspace.tasks.dates'),
      cellRenderer: taskCellRenderers.dates,
      width: 180,
      sortable: false,
      wrapText: false,
      autoHeight: false
    },
    {
      field: 'status',
      headerName: t('workspace.tasks.status'),
      cellRenderer: taskCellRenderers.status,
      width: 200,
      sortable: true,
      wrapText: false,
      autoHeight: false
    }
  )
  
  return columns
})

// Action definitions for tasks grid
const taskActions = computed(() => {
  const actions = []
  
  // Edit button
  if (hasPermissionForProject(PermissionType.UPDATE_TASK, props.projectId).value) {
    actions.push({
      name: 'edit',
      label: t('common.edit'),
      icon: 'fas fa-edit',
      class: 'edit-btn',
      handler: (data: any) => editTask(data._originalData)
    })
  }
  
  // Change Status button
  if (hasPermissionForProject(PermissionType.UPDATE_TASK, props.projectId).value) {
    actions.push({
      name: 'changeStatus',
      label: t('taskWorkspace.statusTransition.changeStatus'),
      icon: 'fas fa-exchange-alt',
      class: 'edit-btn',
      handler: (data: any) => openTaskStatusModal(data._originalData)
    })
  }
  
  // Delete button
  if (hasPermissionForProject(PermissionType.DELETE_TASK, props.projectId).value) {
    actions.push({
      name: 'delete',
      label: t('common.delete'),
      icon: 'fas fa-trash',
      class: 'delete-btn',
      handler: (data: any) => deleteTask(data.id)
    })
  }
  
  return actions
})

// Handle task grid actions
const handleTaskAction = (action: any, data: any) => {
  action.handler(data)
}

// Handle task row clicks
const handleTaskRowClick = (event: any) => {
  selectTask(event.data._originalData)
}

const formatCurrency = async (amount: number, key?: string) => {
  if (amount === null || amount === undefined) return '-'
  try {
    const formatted = await formatCurrencyWithUserPreference(amount)
    if (key) {
      formattedAmounts.value[key] = formatted
    }
    return formatted
  } catch (err) {
    console.warn('Error formatting currency:', err)
    return amount.toString()
  }
}

const getFormattedAmount = (key: string) => {
  return formattedAmounts.value[key] || '...'
}

// Format amounts when tasks change
const formatAllAmounts = async () => {
  try {
    for (const task of searchAndFilteredTasks.value) {
      if (task.id && task.amount !== undefined) {
        const key = `task-${task.id}`
        await formatCurrency(task.amount, key)
      }
    }
  } catch (error) {
    console.warn('Error formatting amounts:', error)
  }
}

// Watch for prop changes to reformat amounts
watch(() => props.tasks, formatAllAmounts, { immediate: true, deep: true })
watch(() => props.filteredTasks, formatAllAmounts, { immediate: true, deep: true })
</script>

<style scoped>
.workspace-tasks-tab {
  padding: 0;
}

/* Search Toolbar */
.workspace-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--neutral-500);
  font-size: 0.875rem;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2.25rem;
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px var(--primary-100);
}

/* Filter Panel */
.filter-panel {
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  padding: 1rem;
}

.filter-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.filter-panel-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.btn-text {
  background: none;
  border: none;
  color: var(--color-primary);
  cursor: pointer;
  font-size: 0.875rem;
  text-decoration: underline;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--neutral-700);
}

.filter-select,
.filter-input {
  padding: 0.5rem;
  border: 1px solid var(--neutral-300);
  border-radius: 4px;
  font-size: 0.875rem;
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 2px var(--primary-100);
}

/* Data Grid */
.data-grid {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.workspace-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.workspace-table thead {
  background: var(--neutral-50);
  border-bottom: 1px solid var(--neutral-200);
}

.workspace-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--neutral-700);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.workspace-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--neutral-200);
  color: var(--neutral-900);
}

.workspace-table tbody tr {
  cursor: pointer;
  transition: background-color 0.15s;
}

.workspace-table tbody tr:hover {
  background: var(--neutral-50);
}

.workspace-table tbody tr.selected {
  background: var(--primary-50);
}

.workspace-table tbody tr:last-child td {
  border-bottom: none;
}

.cell-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.status-pending { background: var(--color-gray-400); }
.status-dot.status-in_progress { background: var(--color-blue-500); }
.status-dot.status-completed { background: var(--color-green-500); }
.status-dot.status-on_hold { background: var(--color-orange-500); }
.status-dot.status-cancelled { background: var(--color-red-500); }

.date-range {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  font-size: 0.875rem;
}

.date-separator {
  color: var(--neutral-400);
  font-size: 0.75rem;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  width: fit-content;
}

.status-badge.status-pending { background: var(--color-gray-100); color: var(--color-gray-700); }
.status-badge.status-in_progress { background: var(--color-blue-100); color: var(--color-blue-700); }
.status-badge.status-completed { background: var(--color-green-100); color: var(--color-green-700); }
.status-badge.status-on_hold { background: var(--color-orange-100); color: var(--color-orange-700); }
.status-badge.status-cancelled { background: var(--color-red-100); color: var(--color-red-700); }

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-icon-small {
  width: 2rem;
  height: 2rem;
  border: none;
  background: var(--neutral-100);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--neutral-600);
}

.btn-icon-small:hover {
  background: var(--neutral-200);
  color: var(--neutral-800);
}

.btn-icon-small.btn-danger {
  color: var(--error-600);
}

.btn-icon-small.btn-danger:hover {
  background: var(--error-100);
  color: var(--error-700);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: var(--neutral-500);
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  color: var(--neutral-500);
}

/* Transitions */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 768px) {
  .workspace-table {
    font-size: 0.875rem;
  }

  .workspace-table th,
  .workspace-table td {
    padding: 0.75rem 0.5rem;
  }

  .cell-content {
    gap: 0.5rem;
  }

  .action-buttons {
    gap: 0.25rem;
  }

  .btn-icon-small {
    width: 1.75rem;
    height: 1.75rem;
    font-size: 0.75rem;
  }
  
  .date-range {
    font-size: 0.8125rem;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }
}
</style>